import React, { useEffect } from 'react'
import { VerificationAgencyClientApplicationService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import { EntriesPerPage } from '../../../../Component/Model/Table/Table';
import { IBulkAction } from '../../../../Component/Common/Components/DataTable/BulkAction';
import { DataTable } from '../../../../Component/Common/Components/DataTable/DataTable';
import { ColumnSortOption } from '../../../../Component/Model/Dto/ColumnSortOption';
import { RelatedPartyTableMapper } from './RelatedPartiesDataTableMapper';
import { RelatedPartySearchInput, RelatedPartySearchResult } from '../../../../Component/Model/Dto/VerificationAgency/Client/Application/RiskAssessment/RelatedParty';
import { ConfirmationDialog } from '../../../../Component/Common/Components/ConfirmationDialog';
import { toast } from 'react-toastify';
import { RelatedPartyModal } from './RelatedPartyModal';

interface IProps {
    riskAssessmentId: number;
}

export const RiskAssessmentRelatedPartiesDataTable = (props: IProps) => {
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const [data, setData] = React.useState<RelatedPartySearchResult[]>([]);
    const [searchValue, setSearchValue] = React.useState<string>("");

    const [selectedRelatedPartyId, setSelectRelatedPartyId] = React.useState(-1);
    const [showRelatedPartyEditModal, setShowRelatedPartyEditModal] = React.useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = React.useState(false);
    const confirmDeleteMessage = "Are you sure you want to delete this related party?";

    const fetchRelatedPartyData = async () => {
        setIsLoading(true);
        try {
            if (props.riskAssessmentId !== -1) {
                const request = {
                    riskAssessmentId: props.riskAssessmentId,
                    columnSortOptions: columnSortOptions,
                    pageNumber: pageNumber,
                    pageSize: entriesPerPage.defaultValue!,
                    searchValue: searchValue
                } as RelatedPartySearchInput;
                const response = await VerificationAgencyClientApplicationService.SearchRelatedParties(request);
                setData(response.data.results);
                setTotalItemsCount(response.data.totalItemsCount);
                setFilteredItemsCount(response.data.filteredItemsCount);
            }
        } catch {
        } finally {
            setIsLoading(false);
        }
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const onAddRelatedPartyClick = () => {
        setSelectRelatedPartyId(-1);
        setShowRelatedPartyEditModal(true);
    };

    const bulkActions: IBulkAction[] = [
        {
            label: 'Add Related Party',
            action: onAddRelatedPartyClick
        }
    ];

    const editRelatedParty = (id: number) => {
        setSelectRelatedPartyId(id);
        setShowRelatedPartyEditModal(true);
    }

    const closeRelatedPartyEditModal = () => {
        setShowRelatedPartyEditModal(false);
        setSelectRelatedPartyId(-1);
        fetchRelatedPartyData();
    }

    const deleteRelatedParty = (id: number) => {
        setSelectRelatedPartyId(id);
        setShowDeleteConfirmationDialog(true);
    }

    const handleDeleteRelatedParty = async () => {
        try {
            await VerificationAgencyClientApplicationService.DeleteRelatedParty(selectedRelatedPartyId);
            toast.success("Successfully deleted Related Party");
            fetchRelatedPartyData();
        } catch (error) {
            console.log(error);
        } finally {
            closeDeleteConfirmation();
        }
    }

    const closeDeleteConfirmation = () => {
        setShowDeleteConfirmationDialog(false);
        setSelectRelatedPartyId(-1);
    }

    useEffect(() => {
        fetchRelatedPartyData();
    }, [props.riskAssessmentId, pageNumber, entriesPerPage, searchValue, columnSortOptions]);

    const tableData = RelatedPartyTableMapper(data, deleteRelatedParty, editRelatedParty);

    return (
        <>
            <DataTable
                table={tableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                canSearch={true}
                searchValueChanged={searchValueChanged}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
                bulkActions={bulkActions}
            />

            {showRelatedPartyEditModal &&
                <RelatedPartyModal
                    relatedPartyId={selectedRelatedPartyId}
                    riskAssessmentId={props.riskAssessmentId}
                    handleClose={closeRelatedPartyEditModal}
                    showModal={showRelatedPartyEditModal}
                />
            }

            <ConfirmationDialog
                showDialog={showDeleteConfirmationDialog}
                handleAccept={handleDeleteRelatedParty}
                handleCancel={closeDeleteConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />
        </>
    )
}
